<script setup>
import { storeToRefs } from 'pinia'
// import IlluSpark02 from '@/assets/illustrations/illu_spark_02.svg?component'
import { useMessageStore } from '@/stores/messages'

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Get center route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch a center list ito check if center route is valid
const { data: centers, suspense: centerSuspense } = useFetchCenterList()

onServerPrefetch(async () => {
    await centerSuspense()
})

const isValidCenterRoute = computed(() => centers.value?.some(item => item.uid === centerRoute.value))

/* kept for the footer app teaser, currently commented out in the template
const appButtonsConfig = computed(() => {
    return {
        iosLink: staticKeys.value?.account_gettheapp_ios_link_footer,
        androidLink: staticKeys.value?.account_gettheapp_android_link_footer,
        trackingContext: 'footer'
    }
})
*/

// Define initial state for navigation
const menuItems = computed(() => {
    return [
        { routeName: isValidCenterRoute.value ? 'centerUid-faq' : 'faq', label: staticKeys.value?.menu_faq },
        { routeName: isValidCenterRoute.value ? 'centerUid-dataProtection' : 'dataProtection', label: staticKeys.value?.menu_dataprotection },
        { routeName: isValidCenterRoute.value ? 'centerUid-legal' : 'legal', label: staticKeys.value?.menu_legal },
        { routeName: isValidCenterRoute.value ? 'centerUid-imprint' : 'imprint', label: staticKeys.value?.menu_imprint },
        { routeName: isValidCenterRoute.value ? 'centerUid-terms' : 'terms', label: staticKeys.value?.menu_agb },
    ]
})
</script>

<template>
    <footer class="page-default-footer">
        <div class="page-default-footer-inner">
            <PageLogo class="page-default-footer-logo" />

            <nav class="page-default-footer-nav">
                <ul class="page-default-footer-nav-list">
                    <li v-for="(item, index) in menuItems" :key="`menu-item-${index}`" class="page-default-footer-nav-list-item">
                        <NuxtLinkLocale :to="{ name: item.routeName }">
                            {{ item.label }}
                        </NuxtLinkLocale>
                    </li>
                </ul>
            </nav>
            <!-- redesign (CN-12074): spark + app teaser removed from the footer for now, kept here in case they return
            <div class="page-default-footer-media">
                <IlluSpark02 />
            </div>

            <div class="page-default-footer-appstore-teaser">
                <h2>{{ staticKeys?.footer_appteaser_headline_web }}</h2>

                <AppDownloadButtonBar class="page-default-footer-appstore-list" :app-download-button-bar-config="appButtonsConfig" />
            </div>
            -->
        </div>
    </footer>
</template>

<style lang="scss">
.page-default-footer {
    background: $page-default-footer-bg;

    @include fullbleed;

    a {
        color: inherit;
        text-decoration: none;
    }
}

.page-default-footer-inner {
    grid-column: 2 / -2;
    padding: px2rem(60) px2rem(32) px2rem(32);
    color: $page-default-footer-txt;
    font-size: px2rem(14);

    @include content-grid($gap: 0);

    @include bp(medium1) {
        display: flex;
        align-items: center;
        gap: px2rem(28); // same gap as between the nav items, so logo-to-nav matches
        padding: px2rem(32) 0;
    }

    @include bp(large1) {
        gap: px2rem(64);
        padding: px2rem(25) 0;
    }
}

.page-default-footer-logo {
    display: flex;
    justify-content: center;
    justify-self: center;
    grid-column: 1 / -1;
    width: px2rem(67); // Figma: ~68x24 wordmark (not the dark header pill)
    height: auto;
    padding: 0;
    margin: 0 0 px2rem(24);
    background-color: transparent;
    border-radius: 0;
    z-index: unset;

    @include bp(medium1) {
        grid-column: 1;
        align-self: center;
        justify-content: flex-start;
        align-items: center;
        margin: 0;
        transform: translateY(px2rem(4)); // optical centering vs the uppercase nav (nudge the wordmark down)
    }

    @include bp(large1) {
        align-items: center;
    }
}

.page-default-footer-nav {
    grid-column: 1 / -1;
    margin-block-end: px2rem(40);

    @include bp(medium1) {
        margin-block-end: 0;
    }
}

.page-default-footer-nav-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    justify-content: space-between;
    align-items: center;
    list-style: none;
    padding: 0;
    margin: 0;

    @include bp(medium1) {
        flex-direction: row;
        justify-content: flex-start;
        gap: px2rem(28);
    }

    @include bp(large1) {
        justify-content: flex-start;
        gap: px2rem(64);
    }
}

.page-default-footer-nav-list-item {
    a,
    span {
        text-transform: uppercase;

        @include body1-medium;

        @include bp(medium1) {
            @include body2-medium;
        }

        &.router-link-active {
            color: $page-default-footer-link-current;
        }

        @media (hover: hover) {
            &:hover {
                color: $page-default-footer-link-hover;
                cursor: pointer;
            }
        }

        @media (hover: none) {
            a:active {
                color: $page-default-footer-link-current;
            }
        }
    }
}

.page-default-footer-media {
    grid-column: -1 / -2;
    grid-row: 2;

    @include bp(medium1) {
        grid-column: 4;
        justify-self: end;
        padding-block-start: 2rem;
    }

    @include bp(large1) {
        display: none;
    }

    svg {
        width: px2rem(32);
        height: auto
    }
}

.page-default-footer-appstore-teaser {
    grid-column: 1 / -1;

    @include bp(large1) {
        display: none;
    }

    h2 {
        font-family: $font-headline;
        font-style: italic;
        font-weight: 500;
        font-size: px2rem(20);
        line-height: px2rem(24);
        letter-spacing: -0.05%;
        text-align: center;
        margin-block: 0 px2rem(40);

        @include bp(medium1) {
            display: none;
        }
    }
}

.page-default-footer-appstore-list {
    display: flex;
    justify-content: center;

    @include bp(medium1) {
        justify-content: flex-start
    }

    img {
        width: 140px;
        height: auto;
    }
}
</style>
